import React, { ButtonHTMLAttributes } from 'react';
import { motion } from 'framer-motion';

type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'outline';

type MotionSafeButtonAttributes = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  'onDrag' | 'onDragStart' | 'onDragEnd' | 'onAnimationStart' | 'onAnimationEnd' | 'onAnimationIteration'
>;

interface ButtonProps extends MotionSafeButtonAttributes {
  variant?: ButtonVariant;
  children: React.ReactNode;
  as?: 'button' | 'a';
  href?: string;
  className?: string;
}

const variantStyles: Record<ButtonVariant, string> = {
  primary:
    'bg-gradient-primary text-white shadow-soft hover:shadow-glow focus:ring-2 focus:ring-primary focus:ring-offset-2 relative overflow-hidden group',
  secondary:
    'bg-[#2a2a32] text-white shadow-soft hover:shadow-soft-lg focus:ring-2 focus:ring-[#2a2a32] focus:ring-offset-2 transition-shadow',
  ghost: 'bg-transparent text-white hover:bg-white/5 focus:ring-2 focus:ring-primary focus:ring-offset-2',
  outline:
    'bg-transparent border-2 border-primary text-primary hover:bg-primary hover:text-white focus:ring-2 focus:ring-primary focus:ring-offset-2 transition-colors duration-200',
};

const tapSpring = { type: 'spring' as const, stiffness: 400, damping: 17 };

export const Button: React.FC<ButtonProps> = ({
  variant = 'primary',
  children,
  as = 'button',
  href,
  className = '',
  type = 'button',
  ...props
}) => {
  const base =
    'inline-flex items-center justify-center rounded-button px-6 py-3 font-medium transition-shadow duration-300 disabled:opacity-50 disabled:cursor-not-allowed relative';

  const combined = `${base} ${variantStyles[variant]} ${className}`.trim();

  if (as === 'a' && href) {
    return (
      <motion.a
        href={href}
        className={combined}
        whileHover={{ scale: 1.03 }}
        whileTap={{ scale: 0.97 }}
        transition={tapSpring}
      >
        {variant === 'primary' && (
          <span className="absolute inset-0 shimmer-bg opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
        )}
        <span className="relative z-10">{children}</span>
      </motion.a>
    );
  }

  return (
    <motion.button
      type={type}
      className={combined}
      whileHover={{ scale: 1.03 }}
      whileTap={{ scale: 0.97 }}
      transition={tapSpring}
      {...props}
    >
      {variant === 'primary' && (
        <span className="absolute inset-0 shimmer-bg opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      )}
      <span className="relative z-10">{children}</span>
    </motion.button>
  );
};
